import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { AgentStatus } from "@/features/chat/types/sdk";
import { useStreamActivityGuard } from "./useStreamActivityGuard";

interface UseAgentStatusResult {
  agentStatus: AgentStatus | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to subscribe to agent status for a project in real-time.
 * Listens to the project document's agent_status field via Firestore onSnapshot.
 *
 * Only subscribes when hasActiveStream is false to avoid conflicts with SSE stream.
 * During SSE streaming, agentStatus remains at its last known value.
 *
 * IMPORTANT: After we've had an active stream in this session, we stop subscribing
 * to Firestore entirely. This prevents the "agent busy" banner from flashing after
 * a normal stream ends (due to Firestore update delay).
 */
export function useAgentStatus(
  userId: string | null,
  projectId: string | null,
  hasActiveStream: boolean
): UseAgentStatusResult {
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use shared guard to determine if we should subscribe
  const { shouldSubscribe } = useStreamActivityGuard(hasActiveStream);

  useEffect(() => {
    // Reset state when IDs change
    setAgentStatus(null);
    setError(null);

    // Don't subscribe if missing required params or guard says no
    if (!userId || !projectId || !shouldSubscribe) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Listen to project document
    const projectRef = doc(db(), "users", userId, "projects", projectId);

    const unsubscribe = onSnapshot(
      projectRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          // Default to WAITING_ON_ANSWER if field doesn't exist (old projects)
          setAgentStatus((data.agent_status as AgentStatus) || "WAITING_ON_ANSWER");
        } else {
          setAgentStatus(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error fetching agent status:", err);
        setError("Could not sync agent status");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, projectId, shouldSubscribe]);

  return { agentStatus, loading, error };
}
